import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react'
import { constant, isEmpty } from 'remeda'
import type { SwymWishlist } from '@velasca/types'
import type { ListItem } from '@velasca/types/src/SwymWishlist'
import { $settings } from '@velasca/utils/globals'

const INIT_TIMEOUT_MS = 250
const WISHLIST_NAME = 'default'

interface WishlistProductData {
  handle: string
  variantId: string
  productId: string
  /** Whether the user explicitly selected this variant (true) or we used the first variant fallback (false). */
  variantSelectedByUser?: boolean
}

interface IWishlistContext {
  isActive: boolean
  isDrawerOpen: boolean
  itemsCount: number
  wishlistItems: ListItem[]
  setIsDrawerOpen: (isOpen: boolean | ((prevState: boolean) => boolean)) => void
  addProduct: (productData: WishlistProductData) => Promise<void>
  removeProduct: (productData: WishlistProductData) => Promise<void>
  getWishlistContent: () => Promise<ListItem[]>
  isProductInWishlist: (productId: string) => boolean
  refreshWishlist: () => Promise<ListItem[]>
}

const WishlistContext = createContext<IWishlistContext | undefined>(undefined)

interface WishlistProviderProps {
  children: React.ReactNode
}

export const WishlistProvider = ({ children }: WishlistProviderProps) => {
  const [isActive, setIsActive] = useState(false)
  const [isDrawerOpen, setIsDrawerOpen] = useState(false)
  const [wishlistId, setWishlistId] = useState<string | undefined>(undefined)
  const [wishlistItems, setWishlistItems] = useState<ListItem[]>([])
  const isLoadingRef = useRef(false)
  const hasFetchedRef = useRef(false)

  useEffect(() => {
    // Override the push function of the SwymCallbacks array to do nothing
    // This prevents Swym from initializing WishlistPlus automatically
    // and inject things like the "Add to Wishlist" button
    window.SwymCallbacks = []
    window.SwymCallbacks.push = constant(0) // Prevent the original push behavior

    // wait for the Swym script to be loaded and initialized
    setTimeout(() => {
      setIsActive(Boolean(window._swat && $settings.wishlistEnabled))
    }, INIT_TIMEOUT_MS)
  }, [])

  const fetchWishlistItems = useCallback(() => {
    if (!isActive || !wishlistId) {
      return Promise.resolve([])
    }

    // Don't fetch if already loading
    if (isLoadingRef.current) {
      return Promise.resolve([])
    }

    isLoadingRef.current = true

    return new Promise<ListItem[]>((resolve, reject) => {
      window._swat?.fetchListCtx(
        { lid: wishlistId },
        (response: ListItem[]) => {
          setWishlistItems(response)
          isLoadingRef.current = false
          resolve(response)
        },
        (error: unknown) => {
          console.error('Error fetching wishlist content', error)
          isLoadingRef.current = false
          reject(error as Error)
        },
      )
    })
  }, [isActive, wishlistId])

  // Fetch wishlist items when wishlistId becomes available
  useEffect(() => {
    if (wishlistId && !hasFetchedRef.current) {
      hasFetchedRef.current = true
      fetchWishlistItems().catch(console.error)
    }
  }, [wishlistId, fetchWishlistItems])

  useEffect(() => {
    if (!isActive) {
      return
    }

    window._swat?.fetchLists({
      callbackFn: (lists: SwymWishlist[]) => {
        const defaultList = lists.find((list) => list.lname === WISHLIST_NAME)

        if (!defaultList) {
          console.warn(`Wishlist with name "${WISHLIST_NAME}" not found.`)
          window._swat?.createList(
            { lname: WISHLIST_NAME },
            (newList: SwymWishlist) => {
              setWishlistId(newList.lid)
            },
            (error: unknown) => {
              console.error('Error creating wishlist', error)
            },
          )
        } else {
          setWishlistId(defaultList.lid)
        }
      },
      errorFn: (error: unknown) => {
        console.error('Error fetching wishlist lists', error)
      },
    })
  }, [isActive])

  const addProduct = useCallback(
    (productData: WishlistProductData) => {
      return new Promise<void>((resolve, reject) => {
        if (!isActive) {
          reject(new Error('Wishlist is not active'))

          return
        }

        if (!wishlistId) {
          reject(new Error('Wishlist is not initialized yet.'))

          return
        }

        const { handle, variantId, productId, variantSelectedByUser } =
          productData

        const swymPayload = {
          epi: Number(
            variantId.replace(/^gid:\/\/shopify\/ProductVariant\//, ''),
          ),
          empi: Number(productId.replace(/^gid:\/\/shopify\/Product\//, '')),
          du: `${window.location.origin}/products/${handle}`,
          cprops: {
            variantSelectedByUser: Boolean(variantSelectedByUser),
          },
        } as const

        window._swat?.addToList(
          wishlistId,
          swymPayload,
          () => {
            // Refresh the wishlist cache after adding a product
            fetchWishlistItems()
              .then(() => {
                resolve()
                setIsDrawerOpen(true) // Open the drawer after adding
              })
              .catch(reject)
          },
          (error: unknown) => {
            console.error('Error adding product to wishlist', error)
            reject(error as Error)
          },
        )
      })
    },
    [isActive, wishlistId, fetchWishlistItems],
  )

  const removeProduct = useCallback(
    (productData: WishlistProductData) => {
      return new Promise<void>((resolve, reject) => {
        if (!isActive) {
          reject(new Error('Wishlist is not active'))

          return
        }

        if (!wishlistId) {
          reject(new Error('Wishlist is not initialized yet.'))

          return
        }

        const { handle, variantId, productId } = productData

        const numericEpi = Number(
          variantId.replace(/^gid:\/\/shopify\/ProductVariant\//, ''),
        )
        const numericEmpi = Number(
          productId.replace(/^gid:\/\/shopify\/Product\//, ''),
        )

        window._swat?.deleteFromList(
          wishlistId,
          {
            epi: numericEpi,
            empi: numericEmpi,
            du: `${window.location.origin}/products/${handle}`,
          },
          () => {
            // Optimistically update local cache so UI (e.g., Wishlist CTA) reflects the change immediately
            setWishlistItems((prev) =>
              prev.filter(
                (item) =>
                  !(item.empi === numericEmpi && item.epi === numericEpi),
              ),
            )

            // Optionally refresh in background to stay in sync with server (non-blocking)
            fetchWishlistItems().catch(() => {
              /* ignore */
            })

            resolve()
          },
          (error: unknown) => {
            console.error('Error removing product from wishlist', error)
            reject(error as Error)
          },
        )
      })
    },
    [isActive, wishlistId, fetchWishlistItems],
  )

  const getWishlistContent = useCallback(() => {
    // Return cached data if available, otherwise fetch fresh data
    if (!isEmpty(wishlistItems)) {
      return Promise.resolve(wishlistItems)
    }

    return fetchWishlistItems()
  }, [wishlistItems, fetchWishlistItems])

  // Synchronous function to check if product is in wishlist using cached data
  const isProductInWishlist = useCallback(
    (productId: string): boolean => {
      if (!isActive || !wishlistId) {
        return false
      }

      // Extract numeric ID from Shopify GID format if needed
      const numericProductId = productId.replace(
        /^gid:\/\/shopify\/Product\//,
        '',
      )

      // Convert to number for comparison (Swym stores empi as number)
      const numericId = parseInt(numericProductId, 10)

      const found = wishlistItems.some((item) => {
        // Check both empi (product ID) and epi (variant ID) fields
        return item.empi === numericId || item.epi === numericId
      })

      return found
    },
    [isActive, wishlistId, wishlistItems],
  )

  return (
    <WishlistContext.Provider
      value={{
        isActive,
        isDrawerOpen,
        itemsCount: wishlistItems.length,
        wishlistItems,
        setIsDrawerOpen,
        addProduct,
        removeProduct,
        getWishlistContent,
        isProductInWishlist,
        refreshWishlist: fetchWishlistItems,
      }}
    >
      {children}
    </WishlistContext.Provider>
  )
}

export const useWishlist = () => {
  const context = useContext(WishlistContext)

  if (!context) {
    throw new Error('useWishlist must be used within a WishlistProvider')
  }

  return context
}
